import { useQuery } from "@tanstack/react-query";
import { Plus, X as XIcon, Star, Sparkle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import ImageUploader from "@/components/ImageUploader";

export const WF_STORY_KEYS = [
  "spotlight", "highlights", "photographer", "photographerLink", "illustrator", "illustratorLink",
  "galleryUrls", "photoGalleryUrls", "webflowAuthorId", "webflowCategoryIds", "sponsorName",
  "sponsorLogoUrl", "sponsorDescription", "sponsorLink", "sponsorShowOnCard", "sponsorMentionOnArticle", "sponsorInlineCard",
];

export const useWebflowRefs = (enabled = true) =>
  useQuery({
    queryKey: ["webflow-refs"],
    queryFn: async () => (await api.get("/webflow/references")).data,
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

export const useWebflowSchema = () =>
  useQuery({ queryKey: ["webflow-schema"], queryFn: async () => (await api.get("/webflow/schema")).data, retry: false });

const F = ({ label, children, req }) => (
  <div className="grid gap-1.5">
    <Label className="text-xs">{label}{req && <span className="ml-0.5 text-bad">*</span>}</Label>
    {children}
  </div>
);

function ToggleRow({ icon: Icon, label, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-xs">
      <span className="flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</span>
      <Switch checked={!!checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function UrlList({ items = [], onChange, disabled, testid, addLabel }) {
  const addUrl = (url) => onChange([...(items || []).filter(Boolean), url]);
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((u, i) => (
        <div key={i} className="flex items-center gap-2">
          {u ? <img src={u} alt="" className="h-9 w-9 shrink-0 rounded border border-border object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} /> : <div className="h-9 w-9 shrink-0 rounded border border-dashed border-border" />}
          <Input value={u} disabled={disabled} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} placeholder="https://…/foto.jpg" className="h-9 font-mono text-[11px]" data-testid={`${testid}-input-${i}`} />
          {!disabled && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Quitar"><XIcon className="h-3.5 w-3.5" /></Button>}
        </div>
      ))}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> {addLabel}</Button>
          <ImageUploader compact testid={`${testid}-uploader`} driveTitle="Elegir imagen en Drive" onUploaded={addUrl} />
        </div>
      )}
    </div>
  );
}

/** Aside: Spotlight / Highlights + Webflow references (Autor, Categoría(s)). */
export function WebflowMetaSection({ f, set, dis }) {
  const { data: refs, isLoading, isError } = useWebflowRefs();
  const connected = refs?.configured && !isError;
  const cats = f.webflowCategoryIds || [];
  const toggleCat = (id) => set("webflowCategoryIds", cats.includes(id) ? cats.filter((c) => c !== id) : [...cats, id]);
  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4" data-testid="story-webflow-meta">
      <div className="kicker">Webflow · Artículos</div>
      <ToggleRow icon={Star} label="Spotlight" checked={f.spotlight} onChange={(v) => set("spotlight", v)} disabled={dis} testid="story-spotlight-switch" />
      <ToggleRow icon={Sparkle} label="Highlights" checked={f.highlights} onChange={(v) => set("highlights", v)} disabled={dis} testid="story-highlights-switch" />
      {connected ? (
        <>
          <F label="Autor (colección Autores)" req>
            <Select value={f.webflowAuthorId || ""} onValueChange={(v) => set("webflowAuthorId", v)} disabled={dis}>
              <SelectTrigger data-testid="story-wf-author-select"><SelectValue placeholder="Elegir autor de Webflow" /></SelectTrigger>
              <SelectContent>{refs.authors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Categoría(s)" req>
            <div className="flex flex-wrap gap-1.5" data-testid="story-wf-categories">
              {refs.categories.map((c) => {
                const on = cats.includes(c.id);
                return (
                  <button key={c.id} type="button" disabled={dis} onClick={() => toggleCat(c.id)}
                    className={cn("rounded-full border px-2.5 py-1 text-[11px] transition-colors duration-150",
                      on ? "border-transparent bg-lilac text-ink" : "border-border bg-background hover:bg-surface-2", dis && "opacity-60")}
                    data-testid={`story-wf-category-${c.slug || c.id}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </F>
        </>
      ) : (
        <div className="rounded-md border border-dashed border-border bg-surface-2 p-3 text-[11.5px] leading-relaxed text-muted-foreground" data-testid="story-wf-refs-offline">
          {isLoading ? "Cargando autores y categorías de Webflow…" : "Webflow no está conectado: al enviar, el Autor y la Categoría se buscarán por nombre (Autor/a y Sección). Al conectar podrás elegirlos aquí."}
        </div>
      )}
    </section>
  );
}

/** Main column: Créditos, Galerías y Patrocinador. */
export function WebflowExtraSections({ f, set, dis }) {
  return (
    <>
      <section className="rounded-lg border border-border bg-card p-4" data-testid="story-credits-section">
        <div className="kicker mb-3">Créditos</div>
        <div className="grid gap-3 md:grid-cols-2">
          <F label="Fotógrafo"><Input value={f.photographer || ""} disabled={dis} onChange={(e) => set("photographer", e.target.value)} data-testid="story-photographer-input" /></F>
          <F label="Fotógrafo Link"><Input value={f.photographerLink || ""} disabled={dis} onChange={(e) => set("photographerLink", e.target.value)} placeholder="https://instagram.com/…" className="font-mono text-[11px]" data-testid="story-photographer-link-input" /></F>
          <F label="Ilustraciones"><Input value={f.illustrator || ""} disabled={dis} onChange={(e) => set("illustrator", e.target.value)} data-testid="story-illustrator-input" /></F>
          <F label="Ilustrador Link"><Input value={f.illustratorLink || ""} disabled={dis} onChange={(e) => set("illustratorLink", e.target.value)} placeholder="https://…" className="font-mono text-[11px]" data-testid="story-illustrator-link-input" /></F>
        </div>
      </section>
      <section className="rounded-lg border border-border bg-card p-4" data-testid="story-galleries-section">
        <div className="kicker mb-3">Galerías</div>
        <div className="grid gap-5 md:grid-cols-2">
          <F label="Galeria"><UrlList items={f.galleryUrls || []} onChange={(v) => set("galleryUrls", v)} disabled={dis} testid="story-gallery" addLabel="Agregar imagen" /></F>
          <F label="Galeria de Fotos"><UrlList items={f.photoGalleryUrls || []} onChange={(v) => set("photoGalleryUrls", v)} disabled={dis} testid="story-photo-gallery" addLabel="Agregar foto" /></F>
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground">Sube desde tu dispositivo, Drive o pega la URL · JPG, PNG, WebP hasta 25 MB · máx. 25 imágenes por galería.</div>
      </section>
      <section className="rounded-lg border border-border bg-card p-4" data-testid="story-sponsor-section">
        <div className="kicker mb-3">Patrocinador</div>
        <div className="grid gap-3 md:grid-cols-2">
          <F label="Patrocinador: Nombre"><Input value={f.sponsorName || ""} disabled={dis} onChange={(e) => set("sponsorName", e.target.value)} data-testid="story-sponsor-name-input" /></F>
          <F label="Patrocinador: Link"><Input value={f.sponsorLink || ""} disabled={dis} onChange={(e) => set("sponsorLink", e.target.value)} placeholder="https://…" className="font-mono text-[11px]" data-testid="story-sponsor-link-input" /></F>
          <F label="Patrocinador: Logo (URL)">
            <div className="flex items-center gap-2">
              {f.sponsorLogoUrl ? <img src={f.sponsorLogoUrl} alt="" className="h-9 w-9 shrink-0 rounded border border-border object-contain" /> : null}
              <Input value={f.sponsorLogoUrl || ""} disabled={dis} onChange={(e) => set("sponsorLogoUrl", e.target.value)} placeholder="https://…/logo.png" className="font-mono text-[11px]" data-testid="story-sponsor-logo-input" />
              {!dis && (
                <ImageUploader
                  compact
                  testid="story-sponsor-logo-uploader"
                  driveTitle="Elegir logo del patrocinador en Drive"
                  onUploaded={(url) => set("sponsorLogoUrl", url)}
                />
              )}
            </div>
          </F>
          <F label="Patrocinador: Descripción"><Textarea rows={2} value={f.sponsorDescription || ""} disabled={dis} onChange={(e) => set("sponsorDescription", e.target.value)} data-testid="story-sponsor-description-input" /></F>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          <ToggleRow label="Show on card" checked={f.sponsorShowOnCard} onChange={(v) => set("sponsorShowOnCard", v)} disabled={dis} testid="story-sponsor-card-switch" />
          <ToggleRow label="Mention on article" checked={f.sponsorMentionOnArticle} onChange={(v) => set("sponsorMentionOnArticle", v)} disabled={dis} testid="story-sponsor-mention-switch" />
          <ToggleRow label="Inline Article Card" checked={f.sponsorInlineCard} onChange={(v) => set("sponsorInlineCard", v)} disabled={dis} testid="story-sponsor-inline-switch" />
        </div>
      </section>
    </>
  );
}

const norm = (v) => (v || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Human preview of what each Webflow field will receive. */
export function previewValue(key, s, refs) {
  const yes = (b) => (b ? "Sí" : "No");
  const imgs = (l) => { const n = (l || []).filter(Boolean).length; return n ? `${n} imagen${n !== 1 ? "es" : ""}` : ""; };
  const authorName = refs?.authors?.find((a) => a.id === s.webflowAuthorId)?.name;
  const catNames = (s.webflowCategoryIds || []).map((id) => refs?.categories?.find((c) => c.id === id)?.name || id);
  switch (key) {
    case "name": return s.title;
    case "slug": return s.slug;
    case "spotlight": return yes(s.spotlight);
    case "highlights": return yes(s.highlights);
    case "coverImage": return s.heroImageUrl;
    case "date": return s.publishDate ? new Date(s.publishDate).toLocaleDateString("es-SV") : "";
    case "author": {
      if (s.webflowAuthorId) return authorName || s.webflowAuthorId;
      if (!s.authorName) return "";
      if (refs?.authors?.length) { const m = refs.authors.find((a) => norm(a.name) === norm(s.authorName)); return m ? `${m.name} (por nombre)` : ""; }
      return `«${s.authorName}» (se buscará por nombre)`;
    }
    case "categories": {
      if (catNames.length) return catNames.join(", ");
      if (!s.section) return "";
      if (refs?.categories?.length) { const m = refs.categories.find((c) => norm(c.name) === norm(s.section)); return m ? `${m.name} (por sección)` : ""; }
      return `«${s.section}» (se buscará por nombre)`;
    }
    case "photographer": return s.photographer;
    case "photographerLink": return s.photographerLink;
    case "illustrator": return s.illustrator;
    case "illustratorLink": return s.illustratorLink;
    case "summary": return s.excerpt;
    case "gallery": return imgs(s.galleryUrls);
    case "body": return s.bodyText ? `${s.bodyText.length} caracteres → HTML` : "";
    case "photoGallery": return imgs(s.photoGalleryUrls);
    case "sponsorName": return s.sponsorName;
    case "sponsorLogo": return s.sponsorLogoUrl;
    case "sponsorDescription": return s.sponsorDescription;
    case "sponsorLink": return s.sponsorLink;
    case "sponsorShowOnCard": return yes(s.sponsorShowOnCard);
    case "sponsorMentionOnArticle": return yes(s.sponsorMentionOnArticle);
    case "sponsorInlineCard": return yes(s.sponsorInlineCard);
    default: return "";
  }
}

// Required fields that are OK to send if empty because the backend fills them in
export const AUTO_FILLED = { date: "Se usará la fecha de hoy" };
